import { useCallback, useEffect, useState } from 'react';
import { getProjectAccess } from '../utils/vfsClient';

/**
 * The caller's access to a project (docs/project-sharing.md): `{ role, owner, members, previewLinks? }`,
 * or null while loading, when sharing is not available (vfs-service unset, an old project, 404) or on
 * any error — callers treat null as "unknown", never as "read only".
 * `setAccess` lets the Share dialog hand back what it just loaded or changed, so the project view
 * follows without a second request.
 */
export default function useProjectAccess(projectId) {
  const [access, setAccess] = useState(null);

  const refresh = useCallback(async () => {
    if (!projectId) {
      setAccess(null);
      return null;
    }
    try {
      const next = await getProjectAccess(projectId);
      setAccess(next);
      return next;
    } catch {
      setAccess(null);
      return null;
    }
  }, [projectId]);

  useEffect(() => {
    let live = true;
    setAccess(null);
    if (!projectId) { return undefined; }
    getProjectAccess(projectId)
      .then((next) => { if (live) { setAccess(next); } })
      .catch(() => { if (live) { setAccess(null); } });
    return () => { live = false; };
  }, [projectId]);

  return { access, role: access?.role || null, refresh, setAccess };
}
